import React, { memo, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';
import { getCurrenciesPairInfo } from 'selectors';
import { getCurrencyCaseByValue, getCurrencyCase } from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import ConvertorLandingPageContent from 'components/ConvertorLandingPageContent';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';
import NbuPageFooter from 'components/NbuPageFooter';

import { Content, Wrapper } from './styles';

const ConvertorLandingPage = memo(({ location, match }) => {
  const intl = useIntl();

  const { source, target } = useSelector(state =>
    getCurrenciesPairInfo(state, { match })
  );

  const defaultMetaTitle = useMemo(() => {
    const { val1 } = match?.params || {};

    if (!val1) {
      return intl.formatMessage({
        id: 'homePage.metaTitle',
      });
    }

    const { currencyValueCaseS } = getCurrencyCaseByValue(source, target, val1);
    const { currencyMnpsT } = getCurrencyCase(source, target, 'mnps');

    return intl.formatMessage(
      {
        id: 'converterLanding.metaTitle',
      },
      {
        value: val1,
        currencyValueCaseS,
        currencyMnpsT,
      }
    );
  }, [intl, match?.params, source, target]);

  const defaultMetaDescription = useMemo(() => {
    const { val1 } = match?.params || {};

    if (!val1) {
      return intl.formatMessage({
        id: 'homePage.metaDescription',
      });
    }

    const { currencyValueCaseS } = getCurrencyCaseByValue(source, target, val1);
    const { currencyMnpsT } = getCurrencyCase(source, target, 'mnps');

    return intl.formatMessage(
      {
        id: 'converterLanding.metaDescription',
      },
      {
        value: val1,
        currencyValueCaseS,
        currencyMnpsT,
      }
    );
  }, [intl, match?.params, source, target]);

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <ConvertorLandingPageContent />
            <PageSidebar isInfoContentDisplayed={false} />
            <NbuPageFooter />
          </PageInner>
        </LayoutContainer>
      </Content>
    </Page>
  );
});

ConvertorLandingPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      val1: PropTypes.string,
      sourceCurrency: PropTypes.string,
      targetCurrency: PropTypes.string,
    }),
  }),
};

ConvertorLandingPage.defaultProps = {
  match: null,
};

export default withRouter(ConvertorLandingPage);
